import * as React from 'react';

import { Checkbox, Label, TextArea } from '@rematter/ui-web';
import { Webhook, Shield, Key, Code } from 'lucide-react';

import { useTranslations } from '../../../../../i18n/useTranslations';
import { NodeConfigProps } from '../../types';

import {
  HttpWebhookTriggerConfigType,
  validateHttpWebhookTriggerConfig,
} from './schema';

/**
 * Configuration panel for HTTP Webhook triggers.
 * Webhook URL and secret are auto-generated by the backend when the automation is created.
 */
export const HttpWebhookTriggerConfig: React.FC<
  NodeConfigProps<HttpWebhookTriggerConfigType>
> = ({ config, onUpdate }) => {
  const t = useTranslations();

  const errors = validateHttpWebhookTriggerConfig(config);

  const handleSchemaChange = (value: string) => {
    onUpdate({ payloadSchema: value || undefined });
  };

  const handleAutocompleteToggle = (isSelected: boolean) => {
    onUpdate({ useSchemaAutocomplete: isSelected });
  };

  return (
    <div className="@HttpWebhookTriggerConfig space-y-4">
      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
        <div className="flex items-start gap-3">
          <Webhook className="mt-0.5 size-5 text-primary" />
          <div className="space-y-2">
            <h4 className="font-medium text-primary">
              {t.editor.triggers.httpWebhook.title}
            </h4>
            <p className="text-sm text-neutral/70">
              {t.editor.triggers.httpWebhook.description}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-neutral/60">
          <Key className="size-4" />
          <span>{t.editor.triggers.httpWebhook.urlGenerated}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-neutral/60">
          <Shield className="size-4" />
          <span>{t.editor.triggers.httpWebhook.signatureEnabled}</span>
        </div>
      </div>

      <div className="rounded-lg border border-neutral/20 bg-neutral/5 p-3">
        <p className="text-xs text-neutral/60">
          <strong>{t.editor.triggers.httpWebhook.afterSaving}</strong>{' '}
          <code className="rounded bg-neutral/10 px-1">
            {t.editor.triggers.httpWebhook.signatureHeader}
          </code>{' '}
          {t.editor.triggers.httpWebhook.header}
        </p>
      </div>

      {/* Payload Schema Section */}
      <div className="space-y-3 border-t border-neutral/20 pt-4">
        <div className="flex items-center gap-2">
          <Code className="size-4 text-neutral/60" />
          <Label className="text-sm font-medium">
            Payload Schema (Optional)
          </Label>
        </div>
        <p className="text-xs text-neutral/60">
          Provide a JSON Schema to enable autocomplete for payload fields. Leave
          empty for dynamic typing.
        </p>
        <TextArea
          value={config.payloadSchema || ''}
          onChange={handleSchemaChange}
          placeholder='{"type": "object", "properties": {"orderId": {"type": "string"}, "amount": {"type": "number"}}}'
          className="font-mono text-xs"
          rows={6}
        />
        {errors.payloadSchema && (
          <p className="text-xs text-failure">{errors.payloadSchema}</p>
        )}

        {config.payloadSchema && (
          <Checkbox
            isSelected={config.useSchemaAutocomplete ?? true}
            onChange={handleAutocompleteToggle}
          >
            <span className="text-sm">Enable payload autocomplete</span>
          </Checkbox>
        )}
      </div>
    </div>
  );
};
